import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import dayjs from 'dayjs';

import Add from '../../components/Add';
import Confirm from '../../components/Confirm';
import Edit from '../../components/Edit';
import { PendingEmptyState, filterPendingRows, useDeleteNotStarted, usePendingQuery } from '../../features/pending';
import '../../features/pending/components/Pending.css';
import { RecordsTable } from '../../features/records';
import '../../features/records/components/Records.css';
import {
    InfiniteScrollList,
    RecordFilterDialog,
    ScrollTopButton,
    TablePagination,
    useRecordFilters,
    useRecordTable,
} from '../../features/shared';
import PendingToolbarSection from './PendingToolbarSection';

function Pending() {
    const { t } = useTranslation(undefined, { keyPrefix: 'Pending' });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const isMobile = useMediaQuery('(max-width: 768px)');

    const filters = useRecordFilters({ initialStart: () => dayjs().subtract(1, 'year') });
    const { data: rows, isPending } = usePendingQuery(filters.range);
    const records = React.useMemo(() => filterPendingRows(rows ?? [], filters.applied), [rows, filters.applied]);
    const table = useRecordTable(records);
    const deleteNotStarted = useDeleteNotStarted();

    const [isFilterOpen, setIsFilterOpen] = React.useState(false);
    const [openAdd, setOpenAdd] = React.useState(false);
    const [openConfig, setOpenConfig] = React.useState(false);
    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [saveAlert, setSaveAlert] = React.useState(false);
    const [alertState, setAlertState] = React.useState({ severity: 'error', message: '' });
    const showAlert = (severity: string, message: string) => {
        setAlertState({ severity, message });
        setSaveAlert(true);
    };
    const closeAlert = (_event?: unknown, reason?: string) => {
        if (reason !== 'clickaway') setSaveAlert(false);
    };

    const handleConfirmDelete = () => {
        deleteNotStarted.mutate(table.selectedDeviceIds, {
            onSuccess: () => showAlert('success', tNotify('deleteSuccess')),
            onError: () => showAlert('error', tNotify('deleteFailed')),
        });
        setOpenConfirm(false);
    };

    const tableProps = {
        tKey: 'Pending',
        selected: table.selected,
        onSelectAll: table.toggleAll,
        isSelected: table.isSelected,
        onRowClick: table.toggleRow,
        onDelete: () => setOpenConfirm(true),
        isMobile,
        mobileDrawerVariant: 'pending',
        onRowEdit: (row: any) => {
            table.selectOnly(row);
            setOpenConfig(true);
        },
        loading: isPending,
    };

    return (
        <div className='PendingPage mt-2 min-h-screen w-full px-4 pb-6 md:mt-8 md:px-6 lg:px-8'>
            <Snackbar open={saveAlert} autoHideDuration={6000} onClose={closeAlert}>
                <Alert onClose={closeAlert} severity={alertState.severity as any}>
                    {alertState.message}
                </Alert>
            </Snackbar>

            <PendingToolbarSection
                isMobile={isMobile}
                searchString={filters.searchString}
                setSearchString={filters.setSearchString}
                onSearch={() => {
                    filters.search();
                    table.setPage(1);
                }}
                onOpenAdd={() => setOpenAdd(true)}
                onOpenEdit={() => {
                    if (table.selected.length === 0) showAlert('warning', t('selectEditMessage'));
                    else setOpenConfig(true);
                }}
                onOpenFilter={() => setIsFilterOpen(true)}
            />
            <RecordFilterDialog
                open={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                filters={filters}
                onChanged={() => table.setPage(1)}
                tKey='Pending.Filter'
            />
            <Add
                openAdd={openAdd}
                setOpenAdd={setOpenAdd}
                setSaveAlert={setSaveAlert}
                setAlertState={setAlertState}
                tKey='Pending.Add'
                handleAddUpdate={filters.extendEndToNow}
                realtime={false}
            />
            <Edit
                openConfig={openConfig}
                setOpenConfig={setOpenConfig}
                setSaveAlert={setSaveAlert}
                setAlertState={setAlertState}
                selectedDeviceId={table.selectedDeviceIds}
                selectedIndex={table.selectedIndexes}
                selectedRecords={table.selectedRows}
                tKey='Pending.Edit'
            />
            <Confirm
                openConfirm={openConfirm}
                setOpenConfirm={setOpenConfirm}
                records={table.selectedDeviceIds}
                handleConfirm={handleConfirmDelete}
                tKey='Pending.Confirm'
            />
            <div className='mt-4'>
                <InfiniteScrollList
                    enabled={isMobile}
                    loaded={table.visibleRecords.length}
                    total={records.length}
                    onLoadMore={table.loadMore}
                >
                    <RecordsTable
                        {...tableProps}
                        paginatedRecords={isMobile ? table.visibleRecords : table.paginatedRecords}
                        emptyContent={<PendingEmptyState />}
                    />
                </InfiniteScrollList>
            </div>
            {!isMobile && <TablePagination page={table.page} pageCount={table.pageCount} onChange={table.changePage} />}
            <ScrollTopButton enabled={isMobile} />
        </div>
    );
}

export default Pending;
